import { Alarm } from '../../../shared/types/mtlinkiTypes.ts';
import { OverlookEntry } from '../../../shared/types/overlookEntry.ts';
import { formatBatchTimeRemaining } from '../../utils/fieldFormats.ts';
import { getStatusColor } from '../../utils/getStatusColor.ts';
import { getBatchTimeRemainingStyle } from '../../utils/styles.ts';


interface OverlookCellProps {
    style?: React.CSSProperties;
    it: OverlookEntry;
}

export function OverlookCell({
    style,
    it,
}: OverlookCellProps) {
    const showWarnings = it.status !== 'DISCONNECT';
    const warningMessage = showWarnings
        ? it.warning ?? (!it.isFullySynced ? 'Not fully synced' : null)
        : null;

    const showPartCounts = it.status !== 'DISCONNECT' && it.partCount != null;
    const potentialScrapText = it.potentialScraps && it.potentialScraps > 0 ? ` (-${it.potentialScraps})` : '';

    const { timeRemaining, timeRemainingStyle } = getTimeRemaining(it);

    const showAlarms = it.status !== 'DISCONNECT' && it.alarms?.length;
    const alarmRows = showAlarms
        ? ensureArrayLength(it.alarms?.filter(it => it.message) ?? [], 4) as Array<Alarm | null>
        : new Array(4).fill(null);


    return (
        <table
            className='equipment-status'
            style={{
                ...style,
                position: 'relative',
                tableLayout: 'fixed',
                width: '100%',
                border: `5px solid ${getStatusColor(it.status)}`,
                backgroundColor: `${getStatusColor(it.status)}18`,
                fontWeight: 'bold',
            }}
        >
            <colgroup>
                <col style={{ width: '50%' }} />
                <col />
                <col style={{ width: '50px' }} />
            </colgroup>
            <tbody>
                <tr>
                    <td title={it.equipment}>{it.equipmentDisplayName}</td>
                    <td style={{ backgroundColor: it.status ? `#${getStatusColor(it.status)}` : '' }}>
                        {it.status}
                    </td>
                    <td>{it.setupOwner}</td>
                </tr>
                <tr>
                    <td title={it.product}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', paddingRight: '4px' }}>
                            <div style={{ flex: 1, overflow: 'hidden', paddingRight: '4px' }}>
                                <div style={{
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                    {it.partDisplayName}
                                </div>
                            </div>
                            {it.produceAtNight && (
                                <img
                                    className='produce-at-night produce-at-night--overlook'
                                    src={'/crescent-moon.png'}
                                />
                            )}
                            {it.isNightReady && (
                                <img
                                    className='night-ready'
                                    src={'/check-green-fat.png'}
                                />
                            )}
                        </div>
                    </td>

                    <td colSpan={2} className={getWarningClass(it.warning, it.status)}>
                        {warningMessage}
                    </td>
                </tr>
                <tr>
                    <td>
                        {showPartCounts && <>
                            {it.partCount}
                            <span style={{ color: 'red' }}>{potentialScrapText}</span>
                            {it.targetQuantity && <span> / {it.targetQuantity}</span>}
                        </>}
                    </td>
                    <td colSpan={2} style={timeRemainingStyle}>{timeRemaining}</td>
                </tr>

                {alarmRows.map((alarm, index) => {
                    return (
                        <tr key={index}>
                            <td colSpan={3} className='alarms-list--cell' title={alarm?.message}>
                                <div className={getAlarmClass(alarm, it.status)}>
                                    {alarm?.message ?? '\u00A0'}
                                </div>
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

/* Filter and pad an array to a specific length */
function ensureArrayLength<T>(arr: T[], length: number): Array<T | null> {
    const filtered = arr.slice(arr.length - length);
    const padding: null[] = Array.from({ length: length - filtered.length }, () => null);
    return [...filtered, ...padding];
}

function getAlarmClass(alarm: Alarm | null, status: string | null): string {
    return !alarm || alarm.end !== null || status === 'DISCONNECT' || status === 'SETUP'
        ? 'equipment-status__alarm--inactive'
        : 'equipment-status__alarm--active';
}

function getWarningClass(warning: string | null, status: string | null): string {
    return warning == null || status === 'DISCONNECT' || status === 'SETUP'
        ? 'equipment-status__alarm--inactive'
        : 'equipment-status__alarm--active';
}

function getTimeRemaining(it: OverlookEntry) {
    const optimisticTimeRemaining = it.batchEndOptimistic ? +it.batchEndOptimistic - Date.now() : null;
    const realisticTimeRemaining = it.batchEndRealistic ? +it.batchEndRealistic - Date.now() : null;

    const showTimeRemaining = it.status !== 'DISCONNECT'
        && optimisticTimeRemaining != null && realisticTimeRemaining != null;
    const timeRemaining = (showTimeRemaining)
        ? formatBatchTimeRemaining(optimisticTimeRemaining, realisticTimeRemaining)
        : '\u00A0';//non-breaking space to keep row from collapsing
    const timeRemainingStyle = showTimeRemaining
        ? getBatchTimeRemainingStyle(optimisticTimeRemaining, it.partCount, it.targetQuantity, it.inOfflineMode)
        : {};
    return { timeRemaining, timeRemainingStyle };
}
